import { fmtDate } from '../../utils/datetime';
import React, { useState, useEffect } from "react";
import { IHeadQuarter, IZone } from "./utils/interface";
import { toast } from '../../utils/toast';
import * as Yup from "yup";
import { useFormik } from "formik";
import { useLoadScript } from "@react-google-maps/api";
import { Plus, Edit3, Trash2, User, Search, X } from "lucide-react";
import PlacesAutocompleteInput from "./utils/PlacesAutocompleteInput";
import config from "../../config";
import { Modal } from "./utils/Modal";
import { createNewHeadquarter, deleteHeadquarter, editHeadquarter, getAllHeadquarters, getAllZones } from "../../api/master";
import { useAuth } from "../../context/AuthContext";

type EditData = {
  head_quarter_id: any,
  zone_id: any,
  latitude: any,
  longitude: any
}

const HeadQuarter: React.FC = () => {
  const libraries: any = ["places"];
  const googleMapsApiKey: string = config.googleMapsApiKey;
  const { user } = useAuth();
  const [headQuarter, setHeadQuarter] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [editData, setEditData] = useState<EditData>({ zone_id: "", head_quarter_id: "", latitude: "", longitude: "" });

  const [zone, setZone] = useState([]);
  const [search, setSearch] = useState("");
  const [zoneFilter, setZoneFilter] = useState("");

  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [placeName, setPlacename] = useState("");
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapsApiKey,
    libraries: libraries,
  });

  useEffect(() => {
    if (user && user.tenant_id) {
      getZones();
      getHeadQuarters();
    }
  }, [user]);

  /**Add New Zone */
  const initialValues = {
    name: "",
    zone_id: "",
  };

  const ZoneSchema = Yup.object({
    name: Yup.string().required("Please enter Zone Name"),
  });
  const {
    setValues,
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    touched,
  } = useFormik({
    initialValues,
    validationSchema: ZoneSchema,
    onSubmit: async (values) => {
      if (!isEdit) {
        try {
          const response = await createNewHeadquarter(values.name, user.tenant_id, lat, lng, values.zone_id);


          if (response.status === 200) {
            getHeadQuarters();
            setIsAddModalOpen(false);
            setValues({ name: "", zone_id: "" });
            toast.success("Headquarter Created Successfully!");
          }
        } catch (error: any) {
          toast.error(error.message || "Something went wrong.");
        }
      }

      if (isEdit) {
        try {
          const response = await editHeadquarter(values.name, user.tenant_id, lat, lng, values.zone_id, editData.head_quarter_id);

          if (response.status === 200) {
            getHeadQuarters();
            setValues({ name: "", zone_id: "" });
            setEditData({ ...editData, zone_id: 0 });
            setIsEdit(false);
            setIsEditModalOpen(false);
            toast.success("Headquarter Updated Successfully!");
          }
        } catch (error: any) {
          toast.error(error.message || "Something went wrong.");
        }
      }
    },
  });

  const getHeadQuarters = async () => {
    try {
      const response = await getAllHeadquarters(user.tenant_id);
      setHeadQuarter(response);
    } catch (error) {
      console.log("Error getHeadQuarters: ", error)
    }
  };
  const getZones = async () => {
    try {
      const response = await getAllZones(user.tenant_id)
      setZone(response);
      console.log(zone);
    } catch (error) { }
  };

  const filteredHQ = (headQuarter as IHeadQuarter[]).filter((item) => {
    const matchSearch = !search || item.name.toLowerCase().includes(search.toLowerCase());
    const matchZone  = !zoneFilter || String(item.zone_id) === zoneFilter;
    return matchSearch && matchZone;
  });

  const handleEdit = (item: IHeadQuarter) => {
    setValues({ name: item.name, zone_id: item.zone_id });
    setEditData({ head_quarter_id: item.id, zone_id: item.zone_id, latitude: item.latitude, longitude: item.longitude });
    setLat(String(item.latitude ?? ""));
    setLng(String(item.longitude ?? ""));
    setPlacename(item.name ?? "");
    setIsEditModalOpen(true);
    setIsEdit(true);
  };

  const handleDelete = async (id: number) => {
    if (!await toast.confirm("This action cannot be undone.", "Delete headquarter?", "Delete")) return;
    try {
      await deleteHeadquarter(id);
      setHeadQuarter(headQuarter.filter((zone: IZone) => zone.id !== id));
      toast.success("Headquarter deleted successfully.");
    } catch (error: any) {
      toast.error(error.message || "Failed to delete headquarter.");
    }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">HeadQuarter Master</h1>
            <p className="text-blue-600 mt-1">Zone </p>
          </div>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add HeadQuarter
          </button>
        </div>

        {/* Master Data List */}
        <div className="bg-white rounded-xl shadow-md">
          <div className="p-6">
            {/* Header + Search + Filter */}
            <div className="flex flex-col justify-between gap-3 mb-6 md:flex-row md:items-center">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 shrink-0">
                <User className="w-5 h-5 text-blue-600" />
                Head Quarter
                <span className="text-sm font-normal text-gray-500">
                  ({filteredHQ.length}{filteredHQ.length !== headQuarter.length ? ` / ${headQuarter.length}` : ''})
                </span>
              </h3>

              <div className="flex flex-col w-full gap-2 sm:flex-row md:w-auto">
                {/* Search */}
                <div className="relative flex-1 sm:w-56">
                  <Search className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none left-3 top-1/2" />
                  <input
                    type="text"
                    placeholder="Search headquarters..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full py-2 pr-8 text-sm border border-gray-300 rounded-lg pl-9 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {search && (
                    <button onClick={() => setSearch("")} className="absolute text-gray-400 -translate-y-1/2 right-2 top-1/2 hover:text-gray-600">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Zone Filter */}
                <select
                  value={zoneFilter}
                  onChange={(e) => setZoneFilter(e.target.value)}
                  className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:w-44"
                >
                  <option value="">All Zones</option>
                  {(zone as IZone[]).map((z) => (
                    <option key={z.id} value={String(z.id)}>{z.name}</option>
                  ))}
                </select>

                {/* Clear */}
                {(search || zoneFilter) && (
                  <button
                    onClick={() => { setSearch(""); setZoneFilter(""); }}
                    className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
                  >
                    <X className="w-3.5 h-3.5" />
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      SN
                    </th>

                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      Name
                    </th>

                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      Latitude
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      Longitude
                    </th>

                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      Updated At
                    </th>

                    <th className="text-left py-3 px-4 font-medium text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHQ.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-sm text-center text-gray-400">
                        {search || zoneFilter ? "No headquarters match the current filters." : "No headquarters found."}
                      </td>
                    </tr>
                  ) : filteredHQ.map((item: IHeadQuarter, index) => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-100 hover:bg-blue-50 transition-colors"
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {index + 1}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {item.name}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {item.latitude}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {item.longitude}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <div className="font-medium text-gray-900">
                            {fmtDate(item.updated_at)}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-2 hover:bg-gray-100 rounded border border-gray-300 transition-colors"
                            title="Edit"
                            type="submit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Add Modal */}
        <Modal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          title={`Add New Head Quarter`}
        >
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Zone *
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              name="zone_id"
              value={values.zone_id}
              onBlur={handleBlur}
              onChange={handleChange}
            >
              <option value="">--Select Zone--</option>
              {zone &&
                zone.map((c: IZone) => {
                  return (
                    <>
                      <option value={c.id}>{c.name}</option>
                    </>
                  );
                })}
            </select>
            {errors.zone_id && touched.zone_id && (
              <p className="text-red-500">{errors.zone_id}</p>
            )}
            <form onSubmit={handleSubmit}>
              <div>
                {isLoaded ? (
                  <PlacesAutocompleteInput
                    onPlaceSelect={(name, lat, lng) => {
                      setLat(lat);
                      setLng(lng);
                      setPlacename(name);
                      setValues({ name, zone_id: values.zone_id });
                    }}
                  />
                ) : (
                  <p className="text-sm text-gray-500 mb-3">Loading maps...</p>
                )}


                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  name="name"
                  value={values.name}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.name && touched.name && (
                  <p className="text-red-500">{errors.name}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Head Quarter
                </button>
              </div>
            </form>
          </div>
        </Modal>

        {/* Edit Modal */}
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          title="Edit Zone"
        >
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Zone *
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              name="zone_id"
              value={values.zone_id}
              onBlur={handleBlur}
              onChange={handleChange}
            >
              <option value="">--Select Zone--</option>
              {zone &&
                zone.map((c: IZone) => {
                  return (
                    <>
                      <option value={c.id}>{c.name}</option>
                    </>
                  );
                })}
            </select>
            {errors.zone_id && touched.zone_id && (
              <p className="text-red-500">{errors.zone_id}</p>
            )}
            <form onSubmit={handleSubmit}>
              <div>
                {isLoaded ? (
                  <PlacesAutocompleteInput
                    onPlaceSelect={(name, lat, lng) => {
                      setLat(lat);
                      setLng(lng);
                      setPlacename(name);
                      setValues({ name, zone_id: values.zone_id });
                    }}
                  />
                ) : (
                  <p className="text-sm text-gray-500 mb-3">Loading maps...</p>
                )}


                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  name="name"
                  value={values.name}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.name && touched.name && (
                  <p className="text-red-500">{errors.name}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default HeadQuarter;
